import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Mail, Phone, Building2, Package, Calendar, Eye, EyeOff, Inbox, RefreshCw, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function ContactCard({ msg, onMark, onDelete }) {
  const unread = !msg.read;
  return (
    <div data-testid={`contact-${msg.message_id}`} className={`rounded-lg border hairline p-5 bg-white card-lift ${unread ? "ring-1 ring-[color:var(--accent-metal)]/40" : ""}`}>
      <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-xl" style={{ color: "var(--primary)" }}>{msg.name}</div>
            {unread && <Badge className="bg-amber-100 text-amber-900 border-amber-200" data-testid={`contact-unread-${msg.message_id}`}>New</Badge>}
          </div>
          <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-3 mt-1 flex-wrap">
            <a href={`mailto:${msg.email}`} className="flex items-center gap-1 hover:text-[color:var(--primary)]"><Mail size={12}/> {msg.email}</a>
            {msg.phone && <a href={`tel:${msg.phone}`} className="flex items-center gap-1 hover:text-[color:var(--primary)]"><Phone size={12}/> {msg.phone}</a>}
            <span className="flex items-center gap-1"><Calendar size={12}/> {formatDate(msg.created_at)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {unread && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onMark(msg.message_id)} data-testid={`mark-read-${msg.message_id}`}>
              <Eye size={14} className="mr-1"/> Mark read
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(msg.message_id)} data-testid={`delete-contact-${msg.message_id}`} className="rounded-full text-rose-700 hover:text-rose-900 hover:bg-rose-50">
            <Trash2 size={14} className="mr-1"/> Delete
          </Button>
        </div>
      </div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--text)" }}>{msg.message}</div>
    </div>
  );
}

function BulkCard({ q, onMark, onDelete }) {
  const unread = !q.read;
  return (
    <div data-testid={`bulk-${q.quote_id}`} className={`rounded-lg border hairline p-5 bg-white card-lift ${unread ? "ring-1 ring-[color:var(--accent-metal)]/40" : ""}`}>
      <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-xl" style={{ color: "var(--primary)" }}>{q.company || q.contact_name}</div>
            {unread && <Badge className="bg-amber-100 text-amber-900 border-amber-200">New</Badge>}
          </div>
          <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-3 mt-1 flex-wrap">
            {q.contact_name && <span className="flex items-center gap-1"><Building2 size={12}/> {q.contact_name}</span>}
            <a href={`mailto:${q.email}`} className="flex items-center gap-1 hover:text-[color:var(--primary)]"><Mail size={12}/> {q.email}</a>
            <span className="flex items-center gap-1"><Package size={12}/> Qty {q.quantity}</span>
            <span className="flex items-center gap-1"><Calendar size={12}/> {formatDate(q.created_at)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {unread && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onMark(q.quote_id)}>
              <Eye size={14} className="mr-1"/> Mark read
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(q.quote_id)} data-testid={`delete-bulk-${q.quote_id}`} className="rounded-full text-rose-700 hover:text-rose-900 hover:bg-rose-50">
            <Trash2 size={14} className="mr-1"/> Delete
          </Button>
        </div>
      </div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--text)" }}>{q.details}</div>
    </div>
  );
}

export default function AdminInbox() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({ contacts: [], bulk_quotes: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("contact");
  const [filter, setFilter] = useState("all"); // all | unread

  const refresh = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/inbox");
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.is_admin) refresh(); }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const markContact = async (id) => { await api.post(`/admin/inbox/contact/${id}/read`); refresh(); };
  const markBulk = async (id) => { await api.post(`/admin/inbox/bulk/${id}/read`); refresh(); };
  const deleteContact = async (id) => {
    if (!window.confirm("Permanently delete this message?")) return;
    try { await api.delete(`/admin/inbox/contact/${id}`); toast.success("Message deleted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const deleteBulk = async (id) => {
    if (!window.confirm("Permanently delete this bulk-quote request?")) return;
    try { await api.delete(`/admin/inbox/bulk/${id}`); toast.success("Quote request deleted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const contacts = filter === "unread" ? data.contacts.filter(m => !m.read) : data.contacts;
  const quotes = filter === "unread" ? data.bulk_quotes.filter(q => !q.read) : data.bulk_quotes;
  const unreadC = data.contacts.filter(m => !m.read).length;
  const unreadB = data.bulk_quotes.filter(q => !q.read).length;

  return (
    <div data-testid="admin-inbox-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em]" style={{ color: "var(--accent-metal)" }}>Admin · Inbox</div>
          <h1 className="font-serif text-4xl" style={{ color: "var(--primary)" }}>Customer Inquiries</h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">Every message submitted from the Contact page and Bulk & Business form lives here.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/orders"><Button variant="outline" size="sm" className="rounded-full">View orders →</Button></Link>
          <Button variant="outline" size="sm" className="rounded-full" onClick={refresh} data-testid="refresh-inbox"><RefreshCw size={14} className="mr-1"/> Refresh</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="inbox-tabs">
        <Button size="sm" variant={tab === "contact" ? "default" : "outline"} className={`rounded-full ${tab === "contact" ? "btn-metal" : ""}`} onClick={() => setTab("contact")} data-testid="tab-contact">
          <Mail size={14} className="mr-2"/> Contact messages {unreadC > 0 && <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-200">{unreadC}</Badge>}
        </Button>
        <Button size="sm" variant={tab === "bulk" ? "default" : "outline"} className={`rounded-full ${tab === "bulk" ? "btn-metal" : ""}`} onClick={() => setTab("bulk")} data-testid="tab-bulk">
          <Building2 size={14} className="mr-2"/> Bulk quotes {unreadB > 0 && <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-200">{unreadB}</Badge>}
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} className={`rounded-full ${filter === "all" ? "btn-metal" : ""}`} onClick={() => setFilter("all")} data-testid="filter-all">All</Button>
          <Button size="sm" variant={filter === "unread" ? "default" : "outline"} className={`rounded-full ${filter === "unread" ? "btn-metal" : ""}`} onClick={() => setFilter("unread")} data-testid="filter-unread"><EyeOff size={14} className="mr-1"/> Unread</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[color:var(--text-muted)]">Loading…</div>
      ) : tab === "contact" ? (
        contacts.length === 0 ? (
          <div className="text-center py-20 rounded-lg border hairline bg-white">
            <Inbox className="mx-auto mb-3 text-[color:var(--text-muted)]" size={32}/>
            <div className="text-sm text-[color:var(--text-muted)]">No contact messages {filter === "unread" ? "that are unread" : "yet"}.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {contacts.map(m => <ContactCard key={m.message_id} msg={m} onMark={markContact} onDelete={deleteContact}/>)}
          </div>
        )
      ) : (
        quotes.length === 0 ? (
          <div className="text-center py-20 rounded-lg border hairline bg-white">
            <Inbox className="mx-auto mb-3 text-[color:var(--text-muted)]" size={32}/>
            <div className="text-sm text-[color:var(--text-muted)]">No bulk quote requests {filter === "unread" ? "that are unread" : "yet"}.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {quotes.map(q => <BulkCard key={q.quote_id} q={q} onMark={markBulk} onDelete={deleteBulk}/>)}
          </div>
        )
      )}
    </div>
  );
}
